/**
 * <StudentActivityPage> — l'élève fait une activité : l'énoncé, sa copie, Sam (page).
 *
 * L'écran d'exécution d'aujourd'hui (`ActivityScreen` + `TimerActivityStep`), en forme A, « la
 * barre de copie » (décidée par Wajih le 15/10) :
 *  - en tête, le retour, le titre, la matière et le chapitre, « Revoir le quiz » s'il y a eu un
 *    quiz de prérequis, et le chrono ;
 *  - l'énoncé prend TOUT l'écran et défile : le texte et ses formules, les images en grand (un
 *    appui les ouvre, zoom compris), le PDF, la vidéo. Sans support, la consigne de l'étape. Il dit
 *    qu'il a une suite (`<StatementScroll>`) : au web sa barre fine, partout un dégradé au bas ;
 *  - en bas, fixe, la barre de copie (`<ActivityCopyBar>`) : un seul gros bouton, « Prendre ma
 *    copie en photo », tant que rien n'est envoyé ; puis les pages déjà envoyées, l'appareil
 *    photo, « J'ai fini ». Elle est dans le flux : elle ne couvre jamais la fin de l'énoncé ;
 *  - Sam (sa tuile, `samTile`) flotte au-dessus de la barre, à droite ; la fin de l'énoncé remonte
 *    au-dessus de lui. En live, c'est la bulle tandem qui flotte là, posée au-dessus des étapes de
 *    l'activité (`<FloatingSlot>`) : la page lui garde sa place sous l'énoncé et la colonne, et lui
 *    déclare sa barre de copie (`useFloatingBase`), au-dessus de laquelle elle se pose. Sa
 *    conversation (`samLayer`, `<SamSheet>`) se pose par-dessus l'énoncé et la barre.
 *
 * Sur un grand écran (le conteneur à partir de `TWO_PANE_FROM`), la copie passe en colonne à
 * droite de l'énoncé (`<TwoPaneTemplate listSide="right">`, `<ActivityCopyColumn>`) et la tuile de
 * Sam flotte au bas de cette colonne, au-dessus de « J'ai fini ». La conversation y devient le
 * panneau latéral de `<SamSheet>` (à partir de 900 px), de la largeur de la colonne : elle la
 * couvre, l'énoncé reste entier à côté, sous le voile.
 *
 * Dans un live (`heading="inline"`, Wajih, 17/10), l'écran n'a qu'un en-tête, celui du live : le
 * titre, la ligne de la matière, « Revoir le quiz » et le chrono ne sont plus une barre, ils
 * ouvrent le contenu et défilent avec l'énoncé. Pas de retour : l'élève ne quitte pas son live, et
 * la liste de ses activités est dans l'en-tête du live.
 *
 * Contrôlée : le chrono, les pages, leur envoi, la page ouverte en grand et sa suppression
 * arrivent en props ; tout geste remonte. L'écran garde ce qui est à lui : le scanner, l'envoi de
 * chaque page, les horloges de Sam, le décompte d'avant le début (l'animation de l'ancien code,
 * gardée telle quelle).
 */
import React, { useEffect, useRef } from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityStatement, type UIStatementReading, type UISubject } from "../../../ports/primary/ui/types"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { ActivityChrono } from "../../molecules/ActivityChrono"
import { ConfirmDialog } from "../../molecules/ConfirmDialog"
import { ConversationHeader } from "../../molecules/ConversationHeader"
import { ImageDisplay } from "../../molecules/ImageDisplay"
import { SAM_TILE_SIZE } from "../../molecules/SamTile"
import { ActivityCopyBar, type ActivityCopyProps } from "../../organisms/ActivityCopyBar"
import { ActivityCopyColumn } from "../../organisms/ActivityCopyColumn"
import { ActivityStatement } from "../../organisms/ActivityStatement"
import { PageTemplate } from "../../templates/PageTemplate"
import { useFloatingBase, useFloatingReserve } from "../../templates/PageTemplate/FloatingSlot"
import { TwoPaneTemplate } from "../../templates/TwoPaneTemplate"
import { type UIActivityChrono } from "../../../ports/primary/ui/types/studentActivity"
import { StatementScroll } from "./StatementScroll"

// Le chrono d'une activité : sa forme vit dans `ports/primary/ui/types` (socle du live).
export type { UIActivityChrono }

/** La copie, telle que la barre et la colonne la lisent — sans leur `testID`. */
export type StudentActivityCopy = Omit<ActivityCopyProps, "testID">

export type StudentActivityPageProps = {
    // ── L'en-tête ──
    title: string
    subject: UISubject
    /** Le chapitre travaillé ; à défaut, la ligne nomme la matière seule, comme aujourd'hui. */
    chapterName?: string
    chrono: UIActivityChrono
    /** Le retour. Absent : pas de flèche (l'activité d'un live). */
    onBack?: () => void
    /**
     * Où se lisent le titre et le chrono. `bar` (défaut) : l'en-tête de l'écran. `inline` : en tête
     * du contenu, ils défilent avec l'énoncé — l'activité d'un live, sous le seul en-tête du live.
     */
    heading?: "bar" | "inline"
    /** L'élève a passé le quiz de prérequis : « Revoir le quiz ». Absent : pas de bouton. */
    onReviewQuiz?: () => void
    /**
     * Une seule colonne de lecture, centrée, de cette largeur (marges comprises) : l'énoncé s'y lit et la
     * rangée de la copie s'y aligne, en pied, quelle que soit la largeur — jamais de volet de copie à
     * droite. Le live au grand écran : la colonne de son volet, ou celle de lecture quand la visio est
     * refermée (Wajih, 19/10 et 22/10). Absente (défaut) : le volet de copie à partir de `TWO_PANE_FROM`.
     */
    column?: number

    // ── L'énoncé ──
    statement: UIActivityStatement
    /** Une figure est annoncée, le serveur la dessine encore. Défaut `false`. */
    figurePending?: boolean
    /** Un PDF de l'énoncé ne s'est pas chargé dans la visionneuse. Absent : rien. */
    onStatementPdfError?: (cause: unknown) => void
    /** La lecture de l'élève (« Ma lecture », `<ActivityStatement reading>`). Absente : la feuille standard. */
    statementReading?: UIStatementReading
    /** Sans support : la consigne de la première étape de l'activité. */
    instruction?: string
    /** Sans support : « Ajouter un énoncé » (le libellé suit le type d'activité). Absent : pas de lien. */
    addMaterial?: { label: string; onPress: () => void }

    // ── La copie ──
    copy: StudentActivityCopy
    /** La page de la copie ouverte en grand, son index. `null` ou absent : aucune. */
    openPhoto?: number | null
    onClosePhoto?: () => void
    /** « Supprimer » dans la visionneuse : l'écran demande confirmation (`confirmDelete`). */
    onDeletePhoto?: (index: number) => void
    /** « Supprimer la photo ? » est posée : la visionneuse se ferme, la question prend sa place. Défaut `false`. */
    confirmDelete?: boolean
    onConfirmDelete?: () => void
    onCancelDelete?: () => void
    /**
     * La hauteur de la barre de copie quand elle est en pied (un téléphone, un volet étroit), et
     * zéro quand elle n'y est plus (la copie passe en colonne, la page se retire) : le live pose
     * ses bandeaux passagers au-dessus d'elle. Rien par défaut.
     */
    onFooterHeight?: (height: number) => void

    // ── Sam ──
    /** Sa tuile, feuille fermée (`<SamFloatingTile>`). Absente : l'activité sans Sam. */
    samTile?: React.ReactNode
    /** Sa conversation ouverte (`<SamSheet>`), par-dessus l'énoncé et la copie. */
    samLayer?: React.ReactNode
    testID?: string
}

/**
 * L'écran d'activité prend toute la fenêtre, pas la colonne d'une page : la copie, à droite, s'y
 * aligne sur le panneau de Sam, qui part du bord de l'écran. C'est l'énoncé qui borne sa lecture.
 * Posée dans un volet (l'activité d'un live, à côté de la scène), elle prend la largeur de ce
 * volet, que le volet lui dit (`PaneWidthContext`) : la fenêtre entière déborderait sur la scène.
 */
export const FULL_WINDOW = Number.POSITIVE_INFINITY
const NOOP = () => {}
/** Une ligne d'énoncé qui se lit d'un trait, sur un grand écran. */
const READING_WIDTH = 760

const TEXTS = {
    instruction: "Consigne",
    reviewQuiz: "Revoir le quiz",
    deleteTitle: "Supprimer la photo ?",
    deleteConfirm: "Supprimer",
    deleteCancel: "Annuler",
    caption: (subject: string, chapter?: string) =>
        chapter != null && chapter !== "" ? `${subject} · ${chapter}` : subject,
}

export function StudentActivityPage(props: StudentActivityPageProps) {
    const {
        title,
        subject,
        chapterName,
        chrono,
        onBack,
        heading = "bar",
        onReviewQuiz,
        column,
        statement,
        figurePending = false,
        onStatementPdfError,
        statementReading,
        instruction,
        addMaterial,
        copy,
        openPhoto = null,
        onClosePhoto,
        onDeletePhoto,
        confirmDelete = false,
        onConfirmDelete,
        onCancelDelete,
        onFooterHeight,
        samTile,
        samLayer,
        testID,
    } = props

    const hasStatement = (statement.text != null && statement.text !== "") || statement.media.length > 0
    // La place de ce qui flotte au bas de l'énoncé : la tuile de Sam, ou la bulle tandem du live.
    const floatingRoom = useFloatingReserve()
    const tileRoom = samTile != null ? SAM_TILE_SIZE : floatingRoom

    const caption = TEXTS.caption(subject.name, chapterName)
    const tools = (
        <Row gap="1" align="center">
            {onReviewQuiz != null && (
                <IconButton
                    iconName="clipboard-check"
                    appearance="transparent"
                    iconColor="primary"
                    size={44}
                    iconSize={20}
                    onPress={onReviewQuiz}
                    accessibilityLabel={TEXTS.reviewQuiz}
                />
            )}
            <ActivityChrono
                remainingSeconds={chrono.remainingSeconds}
                estimatedSeconds={chrono.estimatedSeconds}
                paused={chrono.paused}
                runningSince={chrono.runningSince}
            />
        </Row>
    )

    const statementBlock = (
        <View style={styles.reading}>
            {heading === "inline" && (
                <Row gap="3" align="center" style={styles.inlineHeading}>
                    <Stack gap="0" style={styles.inlineTitle}>
                        <Text size="text-lg" weight="bold" color="text-strong" accessibilityRole="header">
                            {title}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {caption}
                        </Text>
                    </Stack>
                    {tools}
                </Row>
            )}
            {hasStatement ? (
                <ActivityStatement
                    statement={statement}
                    mediaLayout="full"
                    figurePending={figurePending}
                    onPdfError={onStatementPdfError}
                    reading={statementReading}
                />
            ) : (
                <Stack gap="3">
                    <SectionLabel>{TEXTS.instruction}</SectionLabel>
                    {instruction != null && instruction !== "" && <Text size="text-lg">{instruction}</Text>}
                    {addMaterial != null && (
                        <View style={styles.start}>
                            <TextButton label={addMaterial.label} onPress={addMaterial.onPress} />
                        </View>
                    )}
                </Stack>
            )}
        </View>
    )

    // L'énoncé au-dessus de la barre de copie : le téléphone, un volet étroit, ou la colonne de lecture
    // (`column`), où l'énoncé et la rangée de la copie se centrent ; la tuile de Sam reste au coin de
    // l'écran, hors de la colonne.
    const columnBody = (
        <View style={styles.fill}>
            <View style={styles.fill}>
                <StatementScroll
                    contentContainerStyle={[styles.statement(tileRoom), column != null && styles.column(column)]}
                >
                    {statementBlock}
                </StatementScroll>
                {samTile != null && (
                    <View style={styles.tile} pointerEvents="box-none">
                        {samTile}
                    </View>
                )}
            </View>
            <MeasuredFooter onHeight={onFooterHeight}>
                <ActivityCopyBar {...copy} column={column} />
            </MeasuredFooter>
        </View>
    )

    const header =
        heading === "bar" ? (
            <ConversationHeader name={title} caption={caption} onBack={onBack} end={tools} />
        ) : undefined

    return (
        <View style={styles.root} testID={testID}>
            <PageTemplate
                header={header}
                scroll={false}
                padding="0"
                maxContentWidth={FULL_WINDOW}
                contentContainerStyle={styles.flush}
                reserveFloating={false}
            >
                {column != null ? (
                    columnBody
                ) : (
                    <TwoPaneTemplate
                        listSide="right"
                        list={() => <ActivityCopyColumn {...copy} floating={samTile} />}
                        detail={(split) =>
                            split ? (
                                <StatementScroll contentContainerStyle={styles.statement(0)}>
                                    {statementBlock}
                                </StatementScroll>
                            ) : (
                                columnBody
                            )
                        }
                    />
                )}
            </PageTemplate>

            {/* La conversation de Sam couvre tout l'écran, en-tête compris, comme aujourd'hui :
                `<SamSheet>` remplit ce conteneur. */}
            {samLayer}

            {/* La question se pose à la place de la page ouverte, comme aujourd'hui (la visionneuse
                se ferme, la boîte s'ouvre) : deux calques l'un sur l'autre se disputeraient le dessus. */}
            {openPhoto != null && copy.photos[openPhoto] != null && !confirmDelete && (
                <ImageDisplay
                    visible
                    images={copy.photos.map((photo) => ({ uri: photo.uri }))}
                    initialIndex={openPhoto}
                    onClose={onClosePhoto ?? NOOP}
                    onDelete={onDeletePhoto}
                />
            )}
            <ConfirmDialog
                visible={confirmDelete}
                title={TEXTS.deleteTitle}
                confirmLabel={TEXTS.deleteConfirm}
                cancelLabel={TEXTS.deleteCancel}
                danger
                onConfirm={onConfirmDelete ?? NOOP}
                onCancel={onCancelDelete ?? NOOP}
            />
        </View>
    )
}

/**
 * Dit la hauteur de ce qu'il porte à chaque mise en page, et zéro quand il disparaît. C'est aussi le
 * bandeau au-dessus duquel se pose ce qui flotte (`useFloatingBase`).
 */
function MeasuredFooter({ onHeight, children }: { onHeight?: (height: number) => void; children: React.ReactNode }) {
    // Le dernier lecteur, pour le zéro du démontage : un lecteur qui change ne doit pas le déclencher.
    const latest = useRef(onHeight)
    latest.current = onHeight
    useEffect(() => () => latest.current?.(0), [])
    const base = useFloatingBase(true)
    return (
        <View
            onLayout={(event) => {
                const height = event.nativeEvent.layout.height
                base(height)
                onHeight?.(height)
            }}
        >
            {children}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flex: 1 },
    /** Le gabarit pose une marge haute et basse : ici la barre de copie touche le bas. */
    flush: { paddingVertical: 0 },
    /** Le téléphone, puis son corps au-dessus du pied : le second ancre la tuile de Sam. */
    fill: { flex: 1, minHeight: 0 },
    statement: (tileRoom: number) => ({
        padding: theme.spacing["4"],
        // Ce qui flotte au bas de l'énoncé (la tuile de Sam, la bulle tandem) : sa fin remonte au-dessus.
        paddingBottom: tileRoom > 0 ? tileRoom + theme.spacing["8"] : theme.spacing["6"],
    }),
    tile: {
        position: "absolute",
        left: theme.spacing["4"],
        right: theme.spacing["4"],
        bottom: theme.spacing["3"],
    },
    start: { alignItems: "flex-start" },
    column: (width: number) => ({ width: "100%" as const, maxWidth: width, alignSelf: "center" as const }),
    reading: { width: "100%", maxWidth: READING_WIDTH, alignSelf: "center" },
    /** Le titre en tête de l'énoncé : une ligne qui ouvre le contenu, pas une barre. */
    inlineHeading: { marginBottom: theme.spacing["4"] },
    inlineTitle: { flex: 1, flexShrink: 1 },
}))
